import React from "react";
import {
  CheckCircle2, FileText, FileCheck2, AlertTriangle, Users, Clock, Send,
  ChevronDown, Sparkles, ArrowRight, ArrowUp, Plus, Download,
} from "lucide-react";
import { DealRoomMuteMenu, MutedPill } from "./DealRoomMuteMenu";
import { useOperationalSignals } from "../../hooks/useOperationalSignals";
import {
  SellerConfidencePill,
  SolicitorResponsivenessPill,
} from "../operational/SignalPills";

/* Light-mode design tokens for the Flow surface */
export const LIGHT = {
  bg: "#F6F7FB",
  bgSubtle: "#FFFFFF",
  cardBg: "#FFFFFF",
  border: "rgba(15,23,42,0.08)",
  borderStrong: "rgba(15,23,42,0.12)",
  textPrimary: "#0F172A",
  textMuted: "#64748B",
  textSubtle: "#94A3B8",
  shadow: "0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.10)",
  shadowSoft: "0 1px 2px rgba(15,23,42,0.03), 0 2px 8px rgba(15,23,42,0.04)",
};

export const cardStyle = {
  background: LIGHT.cardBg,
  border: `1px solid ${LIGHT.border}`,
  boxShadow: LIGHT.shadowSoft,
};

export const initialsOf = (s) => {
  if (!s) return "?";
  return String(s).trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
};

/* Light-mode momentum chip — used in header + pipeline cards. */
const MOMENTUM_LIGHT = {
  accelerating: { bg: "#ECFDF5", fg: "#059669", border: "#A7F3D0", dot: "#10B981" },
  stable:       { bg: "#EEF2FF", fg: "#4F46E5", border: "#C7D2FE", dot: "#8B5CF6" },
  slowing:      { bg: "#FFFBEB", fg: "#A16207", border: "#FDE68A", dot: "#F59E0B" },
  at_risk:      { bg: "#FEF2F2", fg: "#B91C1C", border: "#FECACA", dot: "#EF4444" },
};

export const MomentumHeaderChip = ({ token = "stable", label, size = "md", testid }) => {
  const t = MOMENTUM_LIGHT[token] || MOMENTUM_LIGHT.stable;
  const display = label || token.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const h = size === "sm" ? "h-[20px]" : "h-[24px]";
  const fs = size === "sm" ? "text-[10px]" : "text-[11px]";
  return (
    <span
      data-testid={testid || `momentum-pill-${token}`}
      className={`${h} ${fs} inline-flex items-center gap-1.5 px-2 rounded-md font-bold tracking-tight`}
      style={{ background: t.bg, color: t.fg, border: `1px solid ${t.border}` }}
      title={`Flow Signal™ · ${display}`}
    >
      <span className="relative inline-flex w-1.5 h-1.5">
        <span className="absolute inset-0 rounded-full" style={{ background: t.dot }} />
        {token === "accelerating" && (
          <span className="absolute inset-0 rounded-full animate-ping" style={{ background: t.dot, opacity: 0.55 }} />
        )}
      </span>
      {display}
    </span>
  );
};

const _ts = (v) => (typeof v === "number" ? v : v ? Date.parse(v) : 0);

export const fmtMoney = (n) => {
  if (!n) return "—";
  const num = typeof n === "number" ? n : parseInt(String(n).replace(/[^0-9]/g, ""), 10) || 0;
  if (!num) return "—";
  return `£${num.toLocaleString()}`;
};

export const fmtDate = (iso) => {
  if (!iso) return "Not set";
  const t = Date.parse(iso);
  if (isNaN(t)) return "Not set";
  return new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

export const daysFromNow = (iso) => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (isNaN(t)) return null;
  return Math.round((t - Date.now()) / 86_400_000);
};

export const fmtTime = (ts) => {
  const d = new Date(_ts(ts));
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
export const fmtDay = (ts) => {
  const t = _ts(ts);
  const diff = (Date.now() - t) / 86_400_000;
  if (diff < 1) return "Today";
  if (diff < 2) return "Yesterday";
  return new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};
export const fmtRel = (ts) => {
  const ms = Date.now() - _ts(ts);
  if (ms < 60_000) return "just now";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/* ─── Status pill ───────────────────────────────────────────────── */
export const StatusPill = ({ status, tone = "neutral" }) => {
  const palette = {
    neutral:   { bg: "#F1F5F9", fg: "#475569", border: "#E2E8F0" },
    awaiting:  { bg: "#FEF3C7", fg: "#A16207", border: "#FDE68A" },
    completed: { bg: "#DCFCE7", fg: "#15803D", border: "#BBF7D0" },
    received:  { bg: "#DCFCE7", fg: "#15803D", border: "#BBF7D0" },
    attention: { bg: "#FFEDD5", fg: "#C2410C", border: "#FED7AA" },
    risk:      { bg: "#FEE2E2", fg: "#B91C1C", border: "#FECACA" },
    update:    { bg: "#EDE9FE", fg: "#6D28D9", border: "#DDD6FE" },
    overdue:   { bg: "#FEE2E2", fg: "#B91C1C", border: "#FECACA" },
    due:       { bg: "#FEF3C7", fg: "#A16207", border: "#FDE68A" },
  }[tone] || { bg: "#F1F5F9", fg: "#475569", border: "#E2E8F0" };
  return (
    <span
      className="inline-flex items-center h-5 px-1.5 rounded text-[9.5px] font-bold uppercase tracking-[0.14em]"
      style={{ background: palette.bg, color: palette.fg, border: `1px solid ${palette.border}` }}
    >
      {status}
    </span>
  );
};

/* ─── Sticky property header ────────────────────────────────────── */
const ROLE_TOKENS = {
  buyer:            { label: "Buyer" },
  seller:           { label: "Seller" },
  buyer_solicitor:  { label: "Buyer Solicitor" },
  seller_solicitor: { label: "Seller Solicitor" },
};

const findParticipant = (participants, role) =>
  (participants || []).find((p) => (p.role || "").toLowerCase() === role);

const RoleChip = ({ label, name, signal }) => (
  <div className="min-w-0">
    <p
      className="text-[12.5px] font-semibold truncate leading-tight"
      style={{ color: name ? LIGHT.textPrimary : LIGHT.textSubtle }}
    >
      {name || "Not assigned"}
    </p>
    <div className="mt-0.5 flex items-center gap-1.5 min-w-0">
      <p className="text-[10.5px] leading-tight truncate" style={{ color: LIGHT.textSubtle }}>{label}</p>
      {signal ? signal : null}
    </div>
  </div>
);

export const PropertyHeader = ({ deal, property, participants, healthScore, momentum, onBack, onInvite, onShare, onUpdate }) => {
  const stageLabel = (() => {
    const m = {
      lead: "Lead",
      qualified: "Qualified",
      viewing: "Viewing",
      offer: "Offer Accepted",
      aml: "AML",
      memo: "Memorandum",
      solicitors: "Solicitors instructed",
      searches: "Searches",
      mortgage_app: "Mortgage App",
      survey: "Survey",
      enquiries: "Enquiries",
      mortgage_offer: "Mortgage Offer",
      exchange_ready: "Pre-Exchange",
      exchanged: "Exchanged",
      completed: "Completed",
    };
    return m[deal.stage] || deal.stage || "—";
  })();

  // Status pill (Moving Well / Requires Attention / Progress Slowing) — derived from chainHealth.
  const status = (() => {
    const ch = healthScore ?? deal.chainHealth ?? 90;
    if (ch >= 75) return { label: "Moving Well",        bg: "#DCFCE7", fg: "#15803D", dot: "#10B981" };
    if (ch >= 50) return { label: "Requires Attention", bg: "#FEF3C7", fg: "#A16207", dot: "#F59E0B" };
    return                { label: "Progress Slowing",  bg: "#FEE2E2", fg: "#B91C1C", dot: "#EF4444" };
  })();

  const buyerSol = findParticipant(participants, "buyer_solicitor");
  const sellerSol = findParticipant(participants, "seller_solicitor");
  // Fallback resolvers — when the deal record itself doesn't carry a
  // buyer/seller link (e.g. Postgres-native demo deals seeded by
  // `seed_demo_celebrity_deals.py`), surface the participant row so the
  // hero never reads "Not assigned" while the party is clearly named in
  // the room.
  const buyerParticipant = findParticipant(participants, "buyer");
  const sellerParticipant = findParticipant(participants, "seller");
  const buyerHeroName  = deal.buyerName  || buyerParticipant?.full_name  || buyerParticipant?.email?.split("@")[0];
  const sellerHeroName = deal.sellerName || sellerParticipant?.full_name || sellerParticipant?.email?.split("@")[0];

  // Embedded operational signals — calm, never alarming. Fetched once
  // per Deal Room (in-memory cached) so the right rail and header
  // share the same data without duplicating requests.
  const { data: opSignals } = useOperationalSignals(deal?.id);
  const sellerSignal = opSignals?.seller_confidence;
  const buyerSolSignal  = (opSignals?.solicitors || []).find((s) => s.role === "buyer_solicitor")?.signal;
  const sellerSolSignal = (opSignals?.solicitors || []).find((s) => s.role === "seller_solicitor")?.signal;

  return (
    <div className="px-5 sm:px-7 lg:px-9 pt-5" data-testid="dealroom-property-header">
      <div className="flex items-center justify-between mb-4 gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold"
          style={{ color: LIGHT.textMuted }}
          data-testid="dealroom-back"
        >
          <ArrowRight size={12} className="rotate-180" /> Back to Deal Rooms
        </button>
      </div>

      <div
        className="rounded-2xl p-4 sm:p-5 grid grid-cols-1 lg:grid-cols-[auto_minmax(0,1fr)_auto] gap-4 lg:gap-6 items-center"
        style={cardStyle}
      >
        {/* Image */}
        <div
          className="w-full max-w-[180px] lg:w-[140px] h-[110px] rounded-xl bg-gradient-to-br from-indigo-200 to-violet-200 flex items-center justify-center shrink-0 overflow-hidden"
          aria-hidden="true"
        >
          {property?.image ? (
            <img src={property.image} alt={property?.address || "Property"} className="w-full h-full object-cover" />
          ) : (
            <span className="text-violet-600 text-[26px] font-bold">{(property?.address || "P").slice(0, 1)}</span>
          )}
        </div>

        {/* Title + meta + role chips */}
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1
              className="text-[20px] sm:text-[24px] font-bold tracking-tight leading-tight truncate"
              style={{ color: LIGHT.textPrimary }}
              data-testid="dealroom-title"
            >
              {property?.address || deal.name || "Transaction"}
            </h1>
            <span
              className="inline-flex items-center gap-1.5 h-[24px] px-2 rounded-md text-[11px] font-bold"
              style={{ background: status.bg, color: status.fg, border: `1px solid ${status.fg}33` }}
              data-testid="dealroom-status-pill"
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: status.dot }} />
              {status.label}
            </span>
            {momentum && (
              <MomentumHeaderChip token={momentum.token} label={momentum.label} />
            )}
          </div>
          <p className="text-[12px] mt-1 truncate" style={{ color: LIGHT.textMuted }}>
            {property?.address ? "" : "—"}
            {property?.address && property?.postcode ? <> — {property.postcode}</> : null}
            <span className="ml-1 text-[11px]" style={{ color: LIGHT.textSubtle }}>
              · {stageLabel} · CF-{(deal.id || "").slice(0, 6).toUpperCase()}
            </span>
          </p>

          {/* Role chips row */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-x-5 gap-y-3">
            <div>
              <p className="text-[14px] font-bold tabular-nums leading-tight" style={{ color: LIGHT.textPrimary }}>
                {fmtMoney(deal.agreedPrice || deal.value)}
              </p>
              <p className="text-[10.5px] mt-0.5" style={{ color: LIGHT.textSubtle }}>Sale Price</p>
            </div>
            <RoleChip label={ROLE_TOKENS.buyer.label}  name={buyerHeroName} />
            <RoleChip
              label={ROLE_TOKENS.seller.label}
              name={sellerHeroName}
              signal={
                sellerSignal && sellerSignal.state !== "Stable" ? (
                  <SellerConfidencePill
                    state={sellerSignal.state}
                    reason={sellerSignal.reasons?.[0] || null}
                    size="xs"
                    testid="dealroom-seller-confidence-pill"
                  />
                ) : null
              }
            />
            <RoleChip
              label={ROLE_TOKENS.buyer_solicitor.label}
              name={buyerSol?.full_name || buyerSol?.email?.split("@")[0]}
              signal={
                buyerSolSignal && buyerSolSignal.state !== "Responsive" ? (
                  <SolicitorResponsivenessPill
                    state={buyerSolSignal.state}
                    reason={buyerSolSignal.reasons?.[0] || null}
                    size="xs"
                    testid="dealroom-buyer-solicitor-responsiveness"
                  />
                ) : null
              }
            />
            <RoleChip
              label={ROLE_TOKENS.seller_solicitor.label}
              name={sellerSol?.full_name || sellerSol?.email?.split("@")[0]}
              signal={
                sellerSolSignal && sellerSolSignal.state !== "Responsive" ? (
                  <SolicitorResponsivenessPill
                    state={sellerSolSignal.state}
                    reason={sellerSolSignal.reasons?.[0] || null}
                    size="xs"
                    testid="dealroom-seller-solicitor-responsiveness"
                  />
                ) : null
              }
            />
          </div>
        </div>

        {/* Right cluster — Share + Update split + overflow */}
        <div className="hidden lg:flex items-start gap-2 shrink-0 self-start">
          <MutedPill dealId={deal?.id} className="self-center mr-1" />
          <button
            type="button"
            onClick={onShare}
            className="cf-room-pill-btn"
            data-testid="dealroom-header-share"
          >
            <ArrowUp size={12} /> Share
          </button>
          <button
            type="button"
            onClick={onUpdate}
            className="cf-room-update-btn"
            data-testid="dealroom-header-update"
          >
            <Plus size={12} strokeWidth={2.6} /> Update <ChevronDown size={12} />
          </button>
          <DealRoomMuteMenu deal={deal} variant="desktop" />
        </div>
      </div>

      {/* Component-scoped premium button styles — refinement only */}
      <style>{`
        .cf-room-pill-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          height: 40px;
          padding: 0 14px;
          border-radius: 6px;
          background: #FFFFFF;
          border: 1px solid ${LIGHT.borderStrong};
          color: ${LIGHT.textPrimary};
          font-size: 12.5px;
          font-weight: 600;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.6), 0 2px 6px -2px rgba(15,23,42,0.06);
          transition:
            transform 220ms cubic-bezier(0.16,1,0.3,1),
            box-shadow 220ms ease,
            border-color 220ms ease,
            background-color 200ms ease;
        }
        .cf-room-pill-btn:hover {
          transform: translateY(-1px);
          border-color: rgba(124,58,237,0.22);
          background: #FAFAFE;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.7),
            0 10px 22px -10px rgba(124,58,237,0.16);
        }
        .cf-room-pill-btn:active { transform: translateY(0); }

        .cf-room-update-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          height: 40px;
          padding-left: 14px;
          padding-right: 12px;
          border-radius: 6px;
          color: #fff;
          font-size: 12.5px;
          font-weight: 700;
          letter-spacing: 0.003em;
          background: linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.18),
            0 10px 24px -10px rgba(99,102,241,0.55),
            0 0 0 1px rgba(124,58,237,0.10);
          transition:
            transform 220ms cubic-bezier(0.16,1,0.3,1),
            box-shadow 220ms ease,
            filter 220ms ease;
        }
        .cf-room-update-btn:hover {
          transform: translateY(-1px);
          filter: brightness(1.04);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.24),
            0 14px 30px -12px rgba(99,102,241,0.65),
            0 0 0 1px rgba(124,58,237,0.18),
            0 0 22px rgba(139,92,246,0.18);
        }
        .cf-room-update-btn:active { transform: translateY(0); filter: brightness(0.98); }
      `}</style>
    </div>
  );
};

/* ─── Health ring ───────────────────────────────────────────────── */
export const HealthRing = ({ value, size = 96, thickness = 8, label }) => {
  const v = Math.max(0, Math.min(100, value || 0));
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const dash = (v / 100) * c;
  const color = v >= 80 ? "#10B981" : v >= 60 ? "#22C55E" : v >= 40 ? "#F59E0B" : "#EF4444";
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#E2E8F0" strokeWidth={thickness} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={thickness} fill="none" strokeLinecap="round" strokeDasharray={`${dash} ${c}`} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className="font-bold tabular-nums" style={{ fontSize: size * 0.32, color: LIGHT.textPrimary }}>{v}</span>
        {label && <span className="text-[9.5px] font-bold uppercase tracking-[0.14em] mt-1" style={{ color }}>{label}</span>}
      </div>
    </div>
  );
};

/* ─── Stage progression (6 nodes) ───────────────────────────────── */
const PROGRESSION = [
  { id: "instruction",  label: "Instruction"  },
  { id: "marketing",    label: "Marketing"    },
  { id: "offer",        label: "Offer Accepted" },
  { id: "conveyancing", label: "Conveyancing" },
  { id: "exchange",     label: "Exchange"     },
  { id: "completion",   label: "Completion"   },
];

const stageGroupIdx = (stage) => {
  if (!stage) return 0;
  if (["lead", "qualified"].includes(stage)) return 0;
  if (["viewing"].includes(stage)) return 1;
  if (["offer"].includes(stage)) return 2;
  if (["aml", "memo", "solicitors", "searches", "mortgage_app", "survey", "enquiries", "mortgage_offer"].includes(stage)) return 3;
  if (["exchange_ready", "exchanged"].includes(stage)) return 4;
  if (["completed"].includes(stage)) return 5;
  return 3;
};

export const StageProgression = ({ deal }) => {
  const idx = stageGroupIdx(deal.stage);
  return (
    <div className="px-5 sm:px-7 lg:px-9 pt-5" data-testid="dealroom-progression">
      <div className="rounded-2xl px-5 py-6" style={cardStyle}>
        <div className="overflow-x-auto cf-scroll">
          <div className="flex items-start min-w-[700px]">
            {PROGRESSION.map((s, i) => {
              const isPast = i < idx;
              const isCurrent = i === idx;
              const isPending = i > idx;
              const dotColor = isPast || isCurrent ? "#10B981" : "#E2E8F0";
              const dotBg = isPast || isCurrent ? "#10B981" : "#FFFFFF";
              return (
                <React.Fragment key={s.id}>
                  <div className="flex flex-col items-center text-center px-1 shrink-0" style={{ minWidth: 96 }}>
                    <span
                      className="relative w-9 h-9 rounded-full flex items-center justify-center"
                      style={{
                        background: isPending ? "#FFFFFF" : dotBg,
                        border: `2px solid ${dotColor}`,
                        boxShadow: isCurrent ? "0 0 0 4px rgba(16,185,129,0.18)" : "none",
                      }}
                      data-testid={`dealroom-stage-${s.id}`}
                    >
                      {isPast ? (
                        <CheckCircle2 size={14} className="text-white" strokeWidth={3} />
                      ) : isCurrent ? (
                        <CheckCircle2 size={14} className="text-white" strokeWidth={3} />
                      ) : (
                        <span className="w-2 h-2 rounded-full" style={{ background: "#CBD5E1" }} />
                      )}
                    </span>
                    <p className="text-[11.5px] font-semibold mt-2" style={{ color: LIGHT.textPrimary }}>
                      {s.label}
                    </p>
                    <p className="text-[10px] mt-1 inline-flex items-center gap-1" style={{ color: isPending ? LIGHT.textSubtle : "#15803D" }}>
                      {(isPast || isCurrent) && <CheckCircle2 size={9} />}
                      {isPast ? "Completed" : isCurrent ? "In Progress" : "Pending"}
                    </p>
                  </div>
                  {i < PROGRESSION.length - 1 && (
                    <span aria-hidden="true" className="flex-1 h-[2px] mt-[18px]"
                      style={{ background: i < idx ? "#10B981" : "#E2E8F0", minWidth: 28 }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Content tabs (Flow / Details / Documents N / Tasks N / Notes) ─── */
export const ContentTabs = ({ active, onChange, counts = {}, onFilter }) => {
  const tabs = [
    { id: "flow",         label: "Flow" },
    { id: "history",      label: "History" },
    { id: "milestones",   label: "Milestones" },
    { id: "golden_path",  label: "Golden Path" },
    { id: "participants", label: "Participants", count: counts.participants },
    { id: "documents",    label: "Documents", count: counts.documents },
    { id: "tasks",        label: "Tasks",     count: counts.tasks },
    { id: "details",      label: "Details" },
    { id: "notes",        label: "Notes" },
  ];
  return (
    <div className="px-5 sm:px-7 lg:px-9 pt-6">
      <div
        className="flex items-center gap-1 overflow-x-auto cf-scroll"
        style={{ borderBottom: `1px solid ${LIGHT.border}` }}
        role="tablist"
        data-testid="dealroom-content-tabs"
      >
        {tabs.map((t) => {
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange(t.id)}
              data-testid={`dealroom-content-tab-${t.id}`}
              className="relative h-11 px-3.5 inline-flex items-center gap-1.5 text-[13px] font-semibold transition-colors shrink-0"
              style={{ color: isActive ? "#6D28D9" : LIGHT.textMuted }}
            >
              {t.label}
              {typeof t.count === "number" && t.count > 0 && (
                <span
                  className="text-[10px] font-bold tabular-nums px-1.5 h-4 inline-flex items-center rounded"
                  style={{
                    background: isActive ? "#F5F3FF" : "#F1F5F9",
                    color: isActive ? "#6D28D9" : LIGHT.textMuted,
                  }}
                >
                  {t.count}
                </span>
              )}
              {isActive && (
                <span
                  aria-hidden="true"
                  className="absolute -bottom-px left-3 right-3 h-[2px] rounded-full"
                  style={{ background: "#7C3AED" }}
                />
              )}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-2 px-2">
          <button
            type="button"
            onClick={onFilter}
            className="hidden sm:inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[11.5px] font-semibold"
            style={{ color: LIGHT.textMuted, border: `1px solid ${LIGHT.border}` }}
          >
            Filter
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Flow card kinds ───────────────────────────────────────────── */
export const KIND_META = {
  document_request:  { Icon: FileText,      iconBg: "#EFF6FF", iconFg: "#3B82F6", title: "Document Requested",  pill: { status: "Awaiting",  tone: "awaiting"  } },
  document_received: { Icon: FileCheck2,    iconBg: "#ECFDF5", iconFg: "#10B981", title: "Document Received",   pill: { status: "Received",  tone: "received"  } },
  milestone:         { Icon: CheckCircle2,  iconBg: "#ECFDF5", iconFg: "#10B981", title: "Milestone Completed", pill: { status: "Completed", tone: "completed" } },
  risk:              { Icon: AlertTriangle, iconBg: "#FFF7ED", iconFg: "#F59E0B", title: "Risk Alert",          pill: { status: "Attention", tone: "attention" } },
  update:            { Icon: Users,         iconBg: "#EDE9FE", iconFg: "#8B5CF6", title: "Update",              pill: { status: "Update",    tone: "update"    } },
  deadline:          { Icon: Clock,         iconBg: "#FEF3C7", iconFg: "#D97706", title: "Deadline Approaching",pill: { status: "Due",       tone: "due"       } },
  ai:                { Icon: Sparkles,      iconBg: "#EDE9FE", iconFg: "#8B5CF6", title: "AI Insight",          pill: { status: "Insight",   tone: "update"    } },
};

/* Detect kind from a message/activity */
export const detectKind = (m) => {
  const body = (m.body || m.text || "").trim();
  const lower = body.toLowerCase();

  // Explicit kind prefixes — set by the composer
  if (/^\[milestone\]/i.test(body))                                  return "milestone";
  if (/^\[document_request\]/i.test(body) || /^\[document\]/i.test(body)) return "document_request";
  if (/^\[document_received\]/i.test(body))                          return "document_received";
  if (/^\[risk\]/i.test(body))                                       return "risk";
  if (/^\[action\]/i.test(body) || /^\[deadline\]/i.test(body))      return "deadline";
  if (/^\[update\]/i.test(body))                                     return "update";

  // Legacy [event] system messages — keyword-classified
  if (m.kind === "system" || /^\[event\]/i.test(body)) {
    if (/risk|delay|overdue|missing|blocked/i.test(lower)) return "risk";
    if (/deadline|due in|approaching|exchange.*risk/i.test(lower)) return "deadline";
    if (/received|signed|completed|cleared|approved|ready/i.test(lower)) {
      if (/document|memo|pof|aml|tac|ta6|ta10|contract|searches/.test(lower)) return "document_received";
      return "milestone";
    }
    if (/request|requested|awaiting|chase/i.test(lower)) return "document_request";
    return "update";
  }
  return "update";
};

/* Strip kind prefix for display */
export const stripKindPrefix = (body) =>
  (body || "").replace(/^\[(event|risk|milestone|document(_request|_received)?|action|deadline|update)\]\s*/i, "");

/* ─── Flow card ────────────────────────────────────────────────── */
const ActionButton = ({ children, primary, "data-testid": testid, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="h-9 px-3 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 transition-colors"
    style={
      primary
        ? { background: "#0F172A", color: "#fff" }
        : { background: "#FFFFFF", border: `1px solid ${LIGHT.border}`, color: LIGHT.textPrimary }
    }
  >
    {children}
  </button>
);

export const FlowCard = ({ kind, time, day, title, body, footer, children, action, testid }) => {
  const meta = KIND_META[kind] || KIND_META.update;
  const Icon = meta.Icon;
  return (
    <div className="grid grid-cols-[64px_24px_minmax(0,1fr)] gap-2 sm:gap-3" data-testid={testid}>
      <div className="text-right">
        {day && <p className="text-[10.5px] font-semibold leading-tight" style={{ color: LIGHT.textPrimary }}>{day}</p>}
        <p className="text-[10.5px] tabular-nums leading-tight" style={{ color: LIGHT.textSubtle }}>{time}</p>
      </div>
      <div className="flex flex-col items-center pt-0.5">
        <span
          className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
          style={{ background: meta.iconBg, border: `1.5px solid ${meta.iconFg}` }}
        >
          <Icon size={11} style={{ color: meta.iconFg }} strokeWidth={2.4} />
        </span>
      </div>
      <article
        className="rounded-2xl p-4 min-w-0"
        style={cardStyle}
      >
        <header className="flex items-start justify-between gap-3 mb-1">
          <h3 className="text-[13.5px] font-bold leading-snug" style={{ color: LIGHT.textPrimary }}>
            {title || meta.title}
          </h3>
          <StatusPill {...meta.pill} />
        </header>
        {body && <p className="text-[12.5px] leading-relaxed" style={{ color: LIGHT.textMuted }}>{body}</p>}
        {children}
        {(footer || action) && (
          <footer className="flex items-center justify-between gap-3 mt-3 flex-wrap">
            <div className="text-[11.5px] flex-1 min-w-0" style={{ color: LIGHT.textMuted }}>{footer}</div>
            {action}
          </footer>
        )}
      </article>
    </div>
  );
};

/* ─── Composer ─────────────────────────────────────────────────── */
const COMPOSER_CHIPS = [
  { id: "milestone", kind: "milestone",        label: "Milestone", Icon: CheckCircle2,  fg: "#10B981", bg: "#ECFDF5" },
  { id: "document",  kind: "document_request", label: "Document",  Icon: FileText,      fg: "#3B82F6", bg: "#EFF6FF" },
  { id: "update",    kind: "update",           label: "Update",    Icon: Users,         fg: "#8B5CF6", bg: "#EDE9FE" },
  { id: "risk",      kind: "risk",             label: "Risk",      Icon: AlertTriangle, fg: "#F59E0B", bg: "#FFF7ED" },
  { id: "action",    kind: "deadline",         label: "Action",    Icon: ArrowUp,       fg: "#0F172A", bg: "#F1F5F9" },
];

export const FlowComposer = ({ onSend, disabled, presetKind, presetText, onTyping }) => {
  const [draft, setDraft] = React.useState("");
  const [activeKind, setActiveKind] = React.useState(null);
  const [sending, setSending] = React.useState(false);
  const inputRef = React.useRef(null);
  const typingTimeoutRef = React.useRef(null);

  // Allow parent (Quick Actions) to preset a kind + text and focus the input
  React.useEffect(() => {
    if (presetKind || presetText) {
      if (presetKind) setActiveKind(presetKind);
      if (presetText) setDraft(presetText);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [presetKind, presetText]);

  // Debounced typing ping — fires on first keystroke + auto-clears after 6s idle.
  const fireTyping = React.useCallback(() => {
    if (!onTyping) return;
    if (!typingTimeoutRef.current) {
      onTyping(true);
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      onTyping(false);
      typingTimeoutRef.current = null;
    }, 6000);
  }, [onTyping]);

  const submit = async () => {
    const body = draft.trim();
    if (!body || sending || disabled) return;
    setSending(true);
    try {
      const prefix = activeKind ? `[${activeKind}] ` : "";
      await onSend(prefix + body);
      setDraft("");
      setActiveKind(null);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
      onTyping?.(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="px-5 sm:px-7 lg:px-9 pt-5 pb-3 sticky bottom-0"
      style={{ background: `linear-gradient(180deg, transparent 0%, ${LIGHT.bg} 30%)` }}
    >
      <div className="rounded-2xl p-3.5" style={cardStyle} data-testid="dealroom-composer">
        {activeKind && (
          <div className="flex items-center gap-2 mb-2.5">
            <span
              className="inline-flex items-center gap-1.5 h-6 px-2 rounded-md text-[10.5px] font-bold uppercase tracking-[0.14em]"
              style={{
                background: COMPOSER_CHIPS.find((c) => c.kind === activeKind)?.bg || "#F1F5F9",
                color: COMPOSER_CHIPS.find((c) => c.kind === activeKind)?.fg || "#475569",
              }}
              data-testid="dealroom-composer-active-kind"
            >
              {COMPOSER_CHIPS.find((c) => c.kind === activeKind)?.label || activeKind}
            </span>
            <button
              type="button"
              onClick={() => setActiveKind(null)}
              className="text-[10.5px] font-semibold"
              style={{ color: LIGHT.textMuted }}
              data-testid="dealroom-composer-clear-kind"
            >
              Clear
            </button>
          </div>
        )}
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); fireTyping(); }}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
          placeholder={activeKind ? `Describe this ${COMPOSER_CHIPS.find((c) => c.kind === activeKind)?.label.toLowerCase()}…` : "Add an update to the Flow…"}
          className="w-full bg-transparent text-[13px] focus:outline-none mb-3"
          style={{ color: LIGHT.textPrimary }}
          data-testid="dealroom-composer-input"
          disabled={disabled || sending}
        />
        <div className="flex items-center gap-2 flex-wrap">
          {COMPOSER_CHIPS.map((c) => {
            const isActive = activeKind === c.kind;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setActiveKind(isActive ? null : c.kind)}
                aria-pressed={isActive}
                className="h-8 px-2.5 rounded-lg text-[11.5px] font-semibold inline-flex items-center gap-1.5 transition-all"
                style={
                  isActive
                    ? { background: c.bg, border: `1px solid ${c.fg}40`, color: c.fg, boxShadow: `0 0 0 2px ${c.fg}1a` }
                    : { background: "#FFFFFF", border: `1px solid ${LIGHT.border}`, color: LIGHT.textPrimary }
                }
                data-testid={`dealroom-composer-chip-${c.id}`}
              >
                <c.Icon size={11} style={{ color: c.fg }} /> {c.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={submit}
            disabled={!draft.trim() || sending || disabled}
            className="ml-auto w-10 h-10 rounded-xl flex items-center justify-center text-white disabled:opacity-40 transition-transform hover:-translate-y-0.5"
            style={{
              background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
              boxShadow: "0 8px 22px -8px rgba(99,102,241,0.55)",
            }}
            aria-label="Send"
            data-testid="dealroom-composer-send"
          >
            <Send size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Bottom KPI bar (light) ────────────────────────────────────── */
export const BottomKPIBar = ({ deal, property, lastActivityTs, mobileOffset = 78 }) => {
  // iter-alignment · P0 · Price SSOT Audit (2026-02-28) — the
  // KPI value is deal Agreed Price; NEVER fall back to
  // `property.price` (Asking Price).
  const value = fmtMoney(deal.agreedPrice || deal.value);
  const numericValue = parseInt(String(deal.agreedPrice || deal.value || 0).replace(/[^0-9]/g, ""), 10) || 0;
  const commission = numericValue ? `£${Math.round(numericValue * 0.015).toLocaleString()}` : "—";
  const daysActive = (() => {
    const t = _ts(deal.createdAt);
    if (!t) return 0;
    return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
  })();
  const ch = deal.chainHealth ?? 90;
  const riskLabel = ch >= 80 ? "Low" : ch >= 60 ? "Medium" : ch >= 40 ? "High" : "Critical";
  const riskTone = ch >= 80 ? "#10B981" : ch >= 60 ? "#D97706" : "#DC2626";

  return (
    <div
      className="fixed right-0 left-0 md:left-[var(--cf-sidebar-w,300px)] transition-[left] duration-300 ease-out z-[80] pointer-events-auto"
      style={{
        bottom: 0,
        background: "rgba(255,255,255,0.92)",
        borderTop: `1px solid ${LIGHT.border}`,
        backdropFilter: "blur(14px) saturate(140%)",
        WebkitBackdropFilter: "blur(14px) saturate(140%)",
        boxShadow: "0 -2px 16px -8px rgba(15,23,42,0.10)",
      }}
      data-testid="dealroom-kpi-bar"
    >
      <style>{`@media (max-width: 767px) { [data-testid="dealroom-kpi-bar"] { bottom: ${mobileOffset}px !important; } }`}</style>
      <div className="overflow-x-auto cf-scroll" style={{ paddingBottom: "max(0px, env(safe-area-inset-bottom, 0px))" }}>
        <div className="flex items-center min-w-max md:min-w-0 md:justify-between px-4 md:px-8 py-3 gap-4">
          <KPI label="Deal Value"      value={value}                            testid="kpi-deal-value" />
          <KPI label="Commission"      value={commission}                       testid="kpi-commission" />
          <KPI label="Days Active"     value={`${daysActive} days`}             testid="kpi-days-active" />
          <KPI label="Exchange Target" value={fmtDate(deal.targetExchange)}     testid="kpi-exchange" />
          <KPI label="Risk Status"     value={riskLabel} dotColor={riskTone}    testid="kpi-risk-status" />
          <KPI label="Last Activity"   value={lastActivityTs ? fmtRel(lastActivityTs) : "—"} dotColor="#10B981" testid="kpi-last-activity" />
        </div>
      </div>
    </div>
  );
};

const KPI = ({ label, value, dotColor, testid }) => (
  <div className="shrink-0" style={{ minWidth: 110 }} data-testid={testid}>
    <p className="text-[9.5px] font-bold uppercase tracking-[0.14em]" style={{ color: LIGHT.textSubtle }}>{label}</p>
    <p className="text-[14px] font-bold tabular-nums mt-1 inline-flex items-center gap-1.5" style={{ color: LIGHT.textPrimary }}>
      {dotColor && <span className="w-1.5 h-1.5 rounded-full" style={{ background: dotColor }} />}
      {value}
    </p>
  </div>
);
